// ─────────────────────────────────────────────────────────────────────────────
//  Interface language (German / English).
//
//  The room carries a default language chosen by the host; each device may
//  override it locally (the TV can be English while the host's laptop is
//  German). `en` is the reference dictionary — `de` must have the same keys,
//  which the `Dict` type enforces at compile time.
// ─────────────────────────────────────────────────────────────────────────────

import type { ThemeId } from "./themes";
import type { Language } from "./types";

export const LANGUAGES: { id: Language; label: string }[] = [
  { id: "en", label: "English" },
  { id: "de", label: "Deutsch" },
];

const en = {
  // ---- common ----
  appName: "OSJ",
  connecting: "Connecting…",
  reconnecting: "Reconnecting…",
  offline: "offline",
  cancel: "Cancel",
  close: "Close",
  save: "Save",
  delete: "Delete",
  back: "Back",
  language: "Language",
  languageThisDevice: "This device",
  languageRoomDefault: "Room default",
  theme: "Theme",
  settings: "Settings",
  copy: "Copy",
  copied: "Copied!",
  roomCode: "Room code",
  players: "Players",
  noPlayers: "No players yet.",
  noPlayersHint: "Nobody has joined yet. Show the QR code so people can scan it.",
  points: "points",

  // ---- landing ----
  landingHeadline: "Open-source Jeopardy",
  landingTagline: "Build your own Jeopardy board, share a four-letter code, and let everyone buzz in from their phone.",
  landingSub: "Free, open source, no accounts and nothing to install. Works on any phone, tablet or TV.",
  startBuilding: "Start building",
  step1Title: "Build the board",
  step1Body: "Categories, point tiles, questions and answers. Add a final wager round if you want one. Everything saves as you type.",
  step2Title: "Share the code",
  step2Body: "You get a four-letter room code and a QR. Put the showcase on a TV; guests scan and they are in. No accounts, no install.",
  step3Title: "Buzz in",
  step3Body: "First lock-in wins the answer. The server decides the order, so the buzzer is fair even over a phone network.",
  sourceCode: "Source",
  createRoom: "Create a room",
  createRoomHint: "Build your questions, then invite everyone.",
  joinRoom: "Join a room",
  joinRoomHint: "Got a code from the host? Enter it here.",
  enterCode: "Enter room code",
  join: "Join",
  roomNotFound: "No room with that code. Check the letters and try again.",
  yourRooms: "Rooms you host",
  yourRoomsHint: "Stored on this device only.",
  reopenAdmin: "Open admin",
  forgetRoom: "Forget",
  forgetRoomConfirm: "Remove this room from this device? The room itself keeps running.",

  // ---- editor ----
  editorTitle: "Build your game",
  editorIntro:
    "Add categories and questions. Everything is saved on this device as you type — export a file if you want a backup.",
  gameTitle: "Game title",
  round: "Round",
  addRound: "Add round",
  removeRound: "Remove round",
  removeRoundConfirm: "Remove this whole round?",
  roundName: "Round name",
  category: "Category",
  addCategory: "Add category",
  removeCategory: "Remove category",
  addQuestion: "Add question",
  removeQuestion: "Remove question",
  questionLabel: "Question (read aloud)",
  answerLabel: "Answer (host only)",
  imageLabel: "Image URL (optional)",
  pointsLabel: "Points",
  finalRound: "Final round (wager)",
  finalRoundHint:
    "One question. Everyone secretly wagers up to their score, types an answer, and all are revealed at once.",
  enableFinal: "Include a final round",
  importPack: "Import file",
  exportPack: "Export file",
  importFailed: "That file could not be read as a game.",
  importRepaired: "Imported with fixes:",
  savedDrafts: "Saved games",
  newDraft: "New game",
  duplicateDraft: "Duplicate",
  deleteDraftConfirm: "Delete this saved game? This cannot be undone.",
  setupTitle: "New game",
  setupIntro: "Pick the shape of the board. You can change any of it later.",
  setupRounds: "Rounds",
  setupCategories: "Categories per round",
  setupRows: "Questions per category",
  setupLadder: "Point ladder",
  setupCreate: "Build the board",
  setupTotal: "{n} question slots",
  questionsWritten: "Written",
  cellEmpty: "empty",
  addRow: "Add row",
  edit: "Edit",
  loadSample: "Load example",
  noRoundsYet: "No rounds yet. Add one, or load the example to see how a board is put together.",
  startHosting: "Create room & host",
  saveToRoom: "Save to room",
  backToAdmin: "Back to admin",
  packEmpty: "Add at least one question before hosting.",
  packWarnEmptyQuestions: "{n} question(s) have no text. They will show up blank.",
  autosaved: "Saved on this device",

  // ---- host / admin ----
  adminTitle: "Admin",
  showQR: "QR code",
  scanToJoin: "Scan to join",
  openShowcase: "Open showcase",
  resetGame: "Reset game",
  resetGameConfirm: "Reset the whole game? All scores to 0, back to round 1.",
  board: "Board",
  backToBoard: "Board",
  answer: "Answer",
  visibleToPlayers: "visible to players",
  onTheClock: "On the clock",
  correct: "Correct",
  wrong: "Wrong",
  showAnswer: "Show answer",
  closeBuzzers: "Close buzzers",
  openBuzzers: "Open buzzers",
  resetBuzzers: "Reset buzzers",
  lockedOutLabel: "Locked out:",
  buzzersLive: "Buzzers live — first to press answers",
  buzzersClosed: "Buzzers closed",
  allLockedOut: "Everyone is locked out — show the answer or reset the buzzers",
  replayHint: "Played tiles stay clickable to replay them",
  jumpToStandings: "Skip to standings",
  jumpToStandingsConfirm: "Jump to the standings?",
  endGame: "End game",
  endGameConfirm: "End the game and show the final scores?",
  setScorePrompt: "Set score for {name}:",
  removePlayerConfirm: "Remove {name}?",
  removePlayer: "Remove player",
  editScore: "Set exact score",
  nextRound: "Start next round",
  toFinalRound: "To the final round",
  newGame: "New game",
  editQuestions: "Edit questions",
  editQuestionsWarn:
    "Replacing the questions resets the board and every score. Players stay in the room.",
  jumpToRound: "Jump to round",

  // ---- wager round ----
  wagers: "Wagers",
  answers: "Answers",
  reveal: "Reveal",
  placeYourWagers: "Place your wagers!",
  writeYourAnswers: "Write your answers!",
  showQuestion: "Show question",
  revealAnswers: "Reveal answers",
  toFinalScores: "To final scores",
  wagerPlaced: "Wager placed",
  waitingForWager: "waiting for a wager…",
  answerSubmitted: "Answer submitted",
  typing: "typing…",
  wagerLabel: "Wager",
  yourWager: "Your wager",
  maxWager: "Max {n}",
  lockInWager: "Lock in wager",
  allIn: "All in",
  yourAnswer: "Your answer",
  submitAnswer: "Submit answer",
  solution: "Solution",
  waitingForHost: "Waiting for the host…",

  // ---- standings ----
  standings: "Standings",
  finalScores: "Final scores",
  roundOver: "{round} complete",
  gameOver: "Game over",
  winner: "Winner",

  // ---- player ----
  joinAs: "Join the game",
  yourName: "Your name",
  shuffleAvatar: "Shuffle avatar",
  letsGo: "Let's go!",
  buzz: "BUZZ",
  buzzed: "Buzzed!",
  youAreUp: "You're up!",
  waitForBuzzers: "Wait for the buzzers…",
  lockedOutYou: "You already answered — locked out",
  someoneElseBuzzed: "{name} buzzed first",
  waitingForQuestion: "Waiting for the next question…",
  yourScore: "Your score",
  leaveRoom: "Leave room",
  leaveRoomConfirm: "Leave this room? Your score stays with the host.",

  // ---- showcase ----
  showcase: "Showcase",
  scanToPlay: "Scan the QR code to play",
  aboutToStart: "Here we go…",
  buzzersOpenBig: "Buzzers open!",

  // ---- errors ----
  connectionLost: "Connection lost. Trying to reconnect…",
  roomClosed: "This room is no longer available.",
  badToken: "This device is not the host of that room.",
  roomFull: "This room is full.",
  imageFailed: "Image could not be loaded",
  imageFailedHint: "(host: please read the question aloud)",
};

export type Dict = typeof en;

const de: Dict = {
  appName: "OSJ",
  connecting: "Verbinde…",
  reconnecting: "Verbinde neu…",
  offline: "offline",
  cancel: "Abbrechen",
  close: "Schließen",
  save: "Speichern",
  delete: "Löschen",
  back: "Zurück",
  language: "Sprache",
  languageThisDevice: "Dieses Gerät",
  languageRoomDefault: "Raum-Standard",
  theme: "Design",
  settings: "Einstellungen",
  copy: "Kopieren",
  copied: "Kopiert!",
  roomCode: "Raum-Code",
  players: "Spieler",
  noPlayers: "Noch keine Spieler.",
  noPlayersHint: "Noch niemand da. Zeig den QR-Code zum Scannen.",
  points: "Punkte",

  landingHeadline: "Open-Source-Jeopardy",
  landingTagline: "Bau dein eigenes Jeopardy-Spielfeld, teile einen vierstelligen Code, und alle buzzern vom Handy.",
  landingSub: "Kostenlos, Open Source, ohne Konto und ohne Installation. Läuft auf Handy, Tablet und Fernseher.",
  startBuilding: "Loslegen",
  step1Title: "Spielfeld bauen",
  step1Body: "Kategorien, Punktefelder, Fragen und Antworten. Auf Wunsch mit Finalrunde. Alles wird beim Tippen gespeichert.",
  step2Title: "Code teilen",
  step2Body: "Du bekommst einen vierstelligen Raum-Code und einen QR-Code. Showcase auf den Fernseher, Gäste scannen, fertig. Kein Konto, keine Installation.",
  step3Title: "Buzzern",
  step3Body: "Wer zuerst drückt, darf antworten. Der Server bestimmt die Reihenfolge — fair auch über Mobilfunk.",
  sourceCode: "Quellcode",
  createRoom: "Raum erstellen",
  createRoomHint: "Fragen bauen, dann alle einladen.",
  joinRoom: "Raum beitreten",
  joinRoomHint: "Code vom Host bekommen? Hier eingeben.",
  enterCode: "Raum-Code eingeben",
  join: "Beitreten",
  roomNotFound: "Kein Raum mit diesem Code. Bitte Buchstaben prüfen.",
  yourRooms: "Deine Räume",
  yourRoomsHint: "Nur auf diesem Gerät gespeichert.",
  reopenAdmin: "Admin öffnen",
  forgetRoom: "Entfernen",
  forgetRoomConfirm: "Raum von diesem Gerät entfernen? Der Raum selbst läuft weiter.",

  editorTitle: "Spiel bauen",
  editorIntro:
    "Kategorien und Fragen hinzufügen. Alles wird beim Tippen auf diesem Gerät gespeichert — exportiere eine Datei als Sicherung.",
  gameTitle: "Spieltitel",
  round: "Runde",
  addRound: "Runde hinzufügen",
  removeRound: "Runde entfernen",
  removeRoundConfirm: "Ganze Runde entfernen?",
  roundName: "Rundenname",
  category: "Kategorie",
  addCategory: "Kategorie",
  removeCategory: "Kategorie entfernen",
  addQuestion: "Frage",
  removeQuestion: "Frage entfernen",
  questionLabel: "Frage (wird vorgelesen)",
  answerLabel: "Antwort (nur Host)",
  imageLabel: "Bild-URL (optional)",
  pointsLabel: "Punkte",
  finalRound: "Finalrunde (Einsatz)",
  finalRoundHint:
    "Eine Frage. Alle setzen geheim bis zu ihrem Punktestand, tippen eine Antwort, alles wird gleichzeitig aufgedeckt.",
  enableFinal: "Finalrunde einbauen",
  importPack: "Datei importieren",
  exportPack: "Datei exportieren",
  importFailed: "Diese Datei konnte nicht als Spiel gelesen werden.",
  importRepaired: "Importiert, mit Korrekturen:",
  savedDrafts: "Gespeicherte Spiele",
  newDraft: "Neues Spiel",
  duplicateDraft: "Duplizieren",
  deleteDraftConfirm: "Gespeichertes Spiel löschen? Das lässt sich nicht rückgängig machen.",
  setupTitle: "Neues Spiel",
  setupIntro: "Wähle die Form des Spielfelds. Alles lässt sich später ändern.",
  setupRounds: "Runden",
  setupCategories: "Kategorien pro Runde",
  setupRows: "Fragen pro Kategorie",
  setupLadder: "Punkteleiter",
  setupCreate: "Spielfeld bauen",
  setupTotal: "{n} Fragen-Felder",
  questionsWritten: "Geschrieben",
  cellEmpty: "leer",
  addRow: "Zeile hinzufügen",
  edit: "Bearbeiten",
  loadSample: "Beispiel laden",
  noRoundsYet: "Noch keine Runden. Füge eine hinzu oder lade das Beispiel, um zu sehen wie ein Spielfeld aufgebaut ist.",
  startHosting: "Raum erstellen & hosten",
  saveToRoom: "Im Raum speichern",
  backToAdmin: "Zurück zum Admin",
  packEmpty: "Mindestens eine Frage anlegen, bevor du hostest.",
  packWarnEmptyQuestions: "{n} Frage(n) haben keinen Text. Sie erscheinen leer.",
  autosaved: "Auf diesem Gerät gespeichert",

  adminTitle: "Admin",
  showQR: "QR-Code",
  scanToJoin: "Zum Mitspielen scannen",
  openShowcase: "Showcase öffnen",
  resetGame: "Zurücksetzen",
  resetGameConfirm: "Ganzes Spiel zurücksetzen? Alle Punkte auf 0, zurück zu Runde 1.",
  board: "Spielfeld",
  backToBoard: "Spielfeld",
  answer: "Antwort",
  visibleToPlayers: "für Spieler sichtbar",
  onTheClock: "Am Zug",
  correct: "Richtig",
  wrong: "Falsch",
  showAnswer: "Antwort zeigen",
  closeBuzzers: "Buzzer schließen",
  openBuzzers: "Buzzer öffnen",
  resetBuzzers: "Buzzer zurücksetzen",
  lockedOutLabel: "Gesperrt:",
  buzzersLive: "Buzzer frei — wer zuerst drückt, antwortet",
  buzzersClosed: "Buzzer gesperrt",
  allLockedOut: "Alle gesperrt — Antwort zeigen oder Buzzer zurücksetzen",
  replayHint: "Gespielte Felder bleiben anklickbar zum Wiederholen",
  jumpToStandings: "Zur Wertung",
  jumpToStandingsConfirm: "Zur Wertung springen?",
  endGame: "Spiel beenden",
  endGameConfirm: "Spiel beenden und Endstand zeigen?",
  setScorePrompt: "Punkte für {name} setzen:",
  removePlayerConfirm: "{name} entfernen?",
  removePlayer: "Spieler entfernen",
  editScore: "Punkte exakt setzen",
  nextRound: "Nächste Runde starten",
  toFinalRound: "Zur Finalrunde",
  newGame: "Neues Spiel",
  editQuestions: "Fragen bearbeiten",
  editQuestionsWarn:
    "Neue Fragen setzen das Spielfeld und alle Punkte zurück. Spieler bleiben im Raum.",
  jumpToRound: "Zu Runde springen",

  wagers: "Einsätze",
  answers: "Antworten",
  reveal: "Auflösung",
  placeYourWagers: "Setzt eure Einsätze!",
  writeYourAnswers: "Schreibt eure Antworten!",
  showQuestion: "Frage zeigen",
  revealAnswers: "Antworten aufdecken",
  toFinalScores: "Zum Endstand",
  wagerPlaced: "Einsatz gesetzt",
  waitingForWager: "wartet auf Einsatz…",
  answerSubmitted: "Antwort abgegeben",
  typing: "tippt…",
  wagerLabel: "Einsatz",
  yourWager: "Dein Einsatz",
  maxWager: "Max. {n}",
  lockInWager: "Einsatz festlegen",
  allIn: "Alles",
  yourAnswer: "Deine Antwort",
  submitAnswer: "Antwort abschicken",
  solution: "Lösung",
  waitingForHost: "Warte auf den Host…",

  standings: "Zwischenstand",
  finalScores: "Endstand",
  roundOver: "{round} beendet",
  gameOver: "Spiel vorbei",
  winner: "Gewinner",

  joinAs: "Mitspielen",
  yourName: "Dein Name",
  shuffleAvatar: "Avatar würfeln",
  letsGo: "Los geht's!",
  buzz: "BUZZER",
  buzzed: "Gebuzzert!",
  youAreUp: "Du bist dran!",
  waitForBuzzers: "Warte auf die Buzzer…",
  lockedOutYou: "Du hast schon geantwortet — gesperrt",
  someoneElseBuzzed: "{name} war schneller",
  waitingForQuestion: "Warte auf die nächste Frage…",
  yourScore: "Deine Punkte",
  leaveRoom: "Raum verlassen",
  leaveRoomConfirm: "Raum verlassen? Deine Punkte bleiben beim Host.",

  showcase: "Showcase",
  scanToPlay: "QR-Code scannen zum Mitspielen",
  aboutToStart: "Gleich geht's los…",
  buzzersOpenBig: "Buzzer frei!",

  connectionLost: "Verbindung verloren. Versuche neu zu verbinden…",
  roomClosed: "Dieser Raum ist nicht mehr verfügbar.",
  badToken: "Dieses Gerät ist nicht der Host dieses Raums.",
  roomFull: "Dieser Raum ist voll.",
  imageFailed: "Bild konnte nicht geladen werden",
  imageFailedHint: "(Host: Frage bitte vorlesen)",
};

const DICTS: Record<Language, Dict> = { en, de };

export function dict(lang: Language): Dict {
  return DICTS[lang] ?? en;
}

export function isLanguage(v: unknown): v is Language {
  return v === "de" || v === "en";
}

/** Fill `{placeholder}` slots: t("removePlayerConfirm", { name: "Alex" }). */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

/** Theme labels need translating too; the theme file only carries English. */
export const THEME_LABELS: Record<Language, Record<ThemeId, string>> = {
  en: {
    classic: "Classic",
    retro: "Retro arcade",
    minimal: "Minimal",
    clay: "Claymation",
    neon: "Neon",
    paper: "Paper",
  },
  de: {
    classic: "Klassisch",
    retro: "Retro-Arcade",
    minimal: "Minimal",
    clay: "Knetmasse",
    neon: "Neon",
    paper: "Papier",
  },
};
